/**
 * What the import watcher should do after a poll — as a pure function.
 *
 * ImportWatcher follows a running import around the app with a pinned toast.
 * Its two bugs were both in THIS decision, not in the rendering:
 *
 *   • It forgot the remembered session the moment a parse finished. That id
 *     is the only way back to the parse — the import screen rejoins through it
 *     — so the toast's "Finish import" button opened an empty upload page.
 *   • It guarded its result toasts with one done-flag for the life of the
 *     layout. The first import set it, so a second import's result was never
 *     shown and its "Parsing…" toast stayed pinned.
 *
 * Pulled out of the component so both are pinned by tests (the frontend has no
 * DOM test environment), the same way import-state.ts holds the flow's store.
 */

export type SessionStatus = "parsing" | "ready" | "failed";

export interface WatchInput {
  /** The remembered session, if any. */
  sessionId: number | null;
  /** The poll errored — the session no longer exists (expired and swept). */
  gone: boolean;
  /** The latest poll's status; null before the first answer. */
  status: SessionStatus | null;
  /** The import screen is showing — it presents all of this itself. */
  onImportScreen: boolean;
  /** The session a failure toast was last shown for. */
  failureShownFor: number | null;
}

export interface WatchDecision {
  toast: "none" | "dismiss" | "progress" | "ready" | "failed";
  /** Stop remembering the session. */
  forget: boolean;
}

export function decideWatch(i: WatchInput): WatchDecision {
  if (i.sessionId === null) return { toast: "none", forget: false };
  // Nothing left to follow.
  if (i.gone) return { toast: "dismiss", forget: true };
  if (i.status === null) return { toast: "none", forget: false };
  if (i.onImportScreen) return { toast: "dismiss", forget: false };
  switch (i.status) {
    // Kept: the import is let go when it is saved or cancelled on the import
    // screen, or discarded from the toast — never just because it is ready.
    case "ready":
      return { toast: "ready", forget: false };
    // Nothing to go back to, so let go now — and say so once per import.
    case "failed":
      return i.failureShownFor === i.sessionId
        ? { toast: "none", forget: false }
        : { toast: "failed", forget: true };
    case "parsing":
      return { toast: "progress", forget: false };
  }
}
